import React from 'react'
import arrayMutators from 'final-form-arrays'
import styled from 'styled-components'
import { get } from 'lodash'
import {
  faCheck,
} from '@fortawesome/free-solid-svg-icons'
import { FieldArray } from 'react-final-form-arrays'

import {
  VARIANT_SECONDARY,
} from '../constants/variants'
import { theme } from '../constants/theme'
import { getFullName } from '../utils/getFullName'
import { useUser } from '../data/UserContext'
import { hasRole } from '../utils/hasRole'
import { ROLES_BO_EVAL } from '../constants/roles'

import { Button, OrangeButton, YellowButton } from './common/Button'
import { Margin } from './common/Margin'
import {
  InputFieldWidth, SmallDateField,
} from './fields/InputField'
import { Form } from './common/Form'
import { H3 } from './common/Typography'
import { Icon } from './common/Icon'
import { GridFieldWidth, UserFieldWidth } from './fields/SelectField'
import { FormLabel } from './common/FormLabel'
import { AddPeople } from './icons/AddPeople'
import { Trad } from './common/Trad'
import { Bin } from './icons/Bin'
import { Field } from './fields/Field'
import { Block } from './common/Block'

const initial = {
  deputiesIds: [],
}

export const IntershipForm = ({
  onSubmit,
  path,
  initialValues = initial,
  loading,
  saveSuccess,
}) => (
  <Form onSubmit={onSubmit} path={path} initialValues={initialValues} mutators={{ ...arrayMutators }}>
    {({ handleSubmit, form }) => {
      const state = form.getState()
      const { values } = state
      const { user } = useUser()
      const canEdit = hasRole(user, ROLES_BO_EVAL) || user.isInternshipDirector
      const canManageTrainees = !!(canEdit || user.internships.find((i) => i.id === values.id))
      const disabled = !canEdit

      return (
        <>
          <Section title="genericInformations">
            <Field
              required
              component={InputFieldWidth}
              name="name"
              disabled={disabled}
              label="internshipName"
              placeholder="internshipNamePlaceholder"
            />
            <SectionRow>
              <Field
                required
                disabled={disabled}
                component={SmallDateField}
                name="startDate"
                label="dates"
              />
              <Field
                required
                disabled={disabled}
                component={SmallDateField}
                name="endDate"
                ghostLabel
              />
            </SectionRow>
            <Field
              required
              disabled={disabled}
              prefilledText={get(initialValues, 'grid.name', '')}
              component={GridFieldWidth}
              name="gridId"
              label="grid"
            />
          </Section>
          <Section title="internshipChiefs">
            <Field
              required
              disabled={disabled}
              prefilledText={initialValues.chief && getFullName({ user: initialValues.chief })}
              component={UserFieldWidth}
              filterOption={({ value }) => values.deputiesIds.indexOf(value) < 0}
              name="chiefId"
              label="internshipChief"
            />
            <SectionRow>
              <FieldArray name="deputiesIds">
                {({ fields }) => (
                  <div>
                    <FormLabel tradId="internshipDeputies" />
                    {fields.map((name, i) => (
                      <SectionRow key={name}>
                        <Field
                          name={name}
                          prefilledText={initialValues.deputy && getFullName({ user: initialValues.deputy })}
                          component={UserFieldWidth}
                          filterOption={({ value }) => value !== values.chiefId}
                          disabled={disabled}
                        />
                        {canEdit && (
                          <OrangeButton
                            icon={<Bin />}
                            onClick={() => fields.remove(i)}
                          />
                        )}
                      </SectionRow>
                    ))}
                    {canEdit && (
                      <AddWrapper>
                        <YellowButton
                          icon={<AddPeople />}
                          onClick={() => fields.push('')}
                        />
                        <Margin h={10} />
                        <Trad id="addDeputy" />
                      </AddWrapper>
                    )}
                  </div>
                )}
              </FieldArray>
            </SectionRow>
          </Section>
          <Section title="trainees">
            <SectionRow>
              <FieldArray name="trainees">
                {({ fields }) => (
                  <div>
                    {fields.map((name, i) => (
                      <SectionRow key={name}>
                        <Field
                          name={`${name}.firstName`}
                          component={InputFieldWidth}
                          placeholder="firstnamePlaceholder"
                          disabled={!canManageTrainees}
                        />
                        <Field
                          name={`${name}.lastName`}
                          component={InputFieldWidth}
                          placeholder="lastnamePlaceholder"
                          disabled={!canManageTrainees}
                        />
                        {canManageTrainees && (
                          <OrangeButton
                            icon={<Bin />}
                            onClick={() => fields.remove(i)}
                          />
                        )}
                      </SectionRow>
                    ))}
                    {canManageTrainees && (
                      <AddWrapper>
                        <YellowButton
                          icon={<AddPeople />}
                          onClick={() => {
                            fields.push({})
                          }}
                        />
                        <Margin h={10} />
                        <Trad id="addTrainee" />
                      </AddWrapper>
                    )}
                  </div>
                )}
              </FieldArray>
            </SectionRow>
          </Section>
          {canManageTrainees && (
            <div>
              <Button
                onClick={handleSubmit}
                loading={loading}
                tradId="save"
                icon={saveSuccess && !state.dirty && <Icon icon={faCheck} />}
                variant={VARIANT_SECONDARY}
              />
            </div>
          )}
        </>
      )
    }}

  </Form>
)

const AddWrapper = styled.div`
  display: flex;
  align-items: center;
`


const Section = ({ title, children }) => (
  <>
    <SectionTitle tradId={title} bold />
    <Margin v={15} />
    <SectionContent border>
      {children}
    </SectionContent>
    <Margin v={30} />
  </>
)

const SectionRow = styled.div`
  width: 100%;
  display: flex;
  align-items: top;
  flex-wrap: wrap;
`
const SectionContent = styled(Block)`
  padding: 23px;
`

const SectionTitle = styled(H3)`
  color: ${theme.colors.blueCommon};
  display: block;
`
